import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { fetchFromApi } from "../../lib/api";
import { fetchImageFromBucket } from "../../lib/utils/fetchImageFromBucket";
import { isUrlExpired } from "../../lib/utils/storage";

interface Illustration {
  id: string;
  storage_path: string;
  title: string;
}

interface WorksPlacement {
  id: string;
  image_id: string;
  works_id: number;
  display_order: number;
  storage_path: string;
  title: string;
}

type WorksGalleryProps = {
  worksId: number;
  isVisible: boolean;
};

export const WorksGallery = ({ worksId, isVisible }: WorksGalleryProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Illustration | null>(null);
  const [visibleIllustrations, setVisibleIllustrations] = useState<
    Illustration[]
  >([]);
  const [thumbnailUrls, setThumbnailUrls] = useState<{ [key: string]: string }>(
    {}
  );
  const [fullSizeUrls, setFullSizeUrls] = useState<{ [key: string]: string }>(
    {}
  );
  const [loadingImages, setLoadingImages] = useState<{
    [key: string]: boolean;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [countImages, setCountImages] = useState(0);

  // 画像の保護機能
  const preventImageDownload = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  // バケットから直接画像を取得
  const fetchImageAndUpdateState = async (
    id: string,
    isFullSize: boolean = false
  ) => {
    const urls = isFullSize ? fullSizeUrls : thumbnailUrls;
    const illustration = visibleIllustrations.find((ill) => ill.id === id);
    if (!illustration) return null;

    const blobUrl = await fetchImageFromBucket(
      id,
      illustration.storage_path,
      isFullSize,
      loadingImages,
      setLoadingImages,
      urls
    );

    if (blobUrl) {
      if (isFullSize) {
        setFullSizeUrls((prev) => ({ ...prev, [id]: blobUrl }));
      } else {
        setThumbnailUrls((prev) => ({ ...prev, [id]: blobUrl }));
      }
    }

    return blobUrl;
  };

  // 画像URLを取得（なければフェッチ、期限切れならリフェッチ）
  const getImageUrl = (
    id: string,
    isFullSize: boolean = false
  ): string | undefined => {
    const urls = isFullSize ? fullSizeUrls : thumbnailUrls;
    const url = urls[id];

    if (!url || isUrlExpired(url)) {
      if (!loadingImages[id] || (url && isUrlExpired(url))) {
        fetchImageAndUpdateState(id, isFullSize);
      }
    }

    return url;
  };

  useEffect(() => {
    const fetchIllustrations = async () => {
      setIsLoading(true);

      try {
        const data = await fetchFromApi(`/works/${worksId}/placements`) as WorksPlacement[];

        if (!data || data.length === 0) {
          setVisibleIllustrations([]);
          setIsLoading(false);
          return;
        }

        const illustrations: Illustration[] = data.map((item) => ({
          id: item.image_id,
          storage_path: item.storage_path,
          title: item.title,
        }));

        setCountImages(illustrations.length);
        setVisibleIllustrations([]);

        const fetchAllThumbnails = async () => {
          for (let i = 0; i < illustrations.length; i += 3) {
            const batch = illustrations.slice(i, i + 3);

            // Note: fetchImageAndUpdateState depends on visibleIllustrations, so we need to set it first or pass illustrations
            setVisibleIllustrations((prev) => [...prev, ...batch]);

            await Promise.all(
              batch.map((illustration) =>
                fetchImageFromBucket(
                  illustration.id,
                  illustration.storage_path,
                  false,
                  loadingImages,
                  setLoadingImages,
                  thumbnailUrls
                ).then(url => {
                  if (url) setThumbnailUrls(prev => ({ ...prev, [illustration.id]: url }));
                })
              )
            );

            if (i + 3 < illustrations.length) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          }
          setIsLoading(false);
        };
        fetchAllThumbnails();
      } catch (error) {
        console.error("Error fetching illustrations:", error);
        setIsLoading(false);
      }
    };

    if (isVisible) {
      fetchIllustrations();
    }
  }, [isVisible, worksId]);

  useEffect(() => {
    return () => {
      [...Object.values(thumbnailUrls), ...Object.values(fullSizeUrls)].forEach(
        (url) => {
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        }
      );
      setThumbnailUrls({});
      setFullSizeUrls({});
    };
  }, [worksId]);

  useEffect(() => {
    if (isModalOpen && selectedImage && !fullSizeUrls[selectedImage.id]) {
      fetchImageAndUpdateState(selectedImage.id, true);
    }
  }, [isModalOpen, selectedImage]);

  if (!isVisible) return null;

  return (
    <>
      {countImages > 0 && isLoading && (
        <p className="mt-4 text-lg font-semibold">{`${countImages} 枚あります...`}</p>
      )}

      {isLoading && visibleIllustrations.length === 0 ? (
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full max-w-4xl mx-auto mt-8"
        >
          {visibleIllustrations.length > 0 ? (
            visibleIllustrations.map((illustration, index) => (
              <motion.div
                key={`${illustration.id}-${index}-thumb`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.05 }}
                className="aspect-square rounded-lg shadow-md cursor-pointer overflow-hidden relative"
                onClick={() => {
                  setSelectedImage(illustration);
                  setIsModalOpen(true);
                }}
              >
                {getImageUrl(illustration.id) ? (
                  <img
                    src={getImageUrl(illustration.id)}
                    className="w-full h-full object-cover select-none pointer-events-none"
                    style={{ WebkitTouchCallout: "none" }}
                    onContextMenu={preventImageDownload}
                    onDragStart={preventImageDownload}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
                  </div>
                )}
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-full text-center py-12"
            >
              <p className="text-2xl font-semibold text-gray-600">
                Coming Soon
              </p>
            </motion.div>
          )}
        </motion.div>
      )}

      <AnimatePresence>
        {isModalOpen && selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="relative max-w-full max-h-full min-h-[50vh] min-w-[50vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full h-full flex items-center justify-center">
                {!fullSizeUrls[selectedImage.id] ? (
                  <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <img
                    src={fullSizeUrls[selectedImage.id]}
                    className="max-w-full max-h-[90vh] object-contain select-none cursor-pointer"
                    style={{ WebkitTouchCallout: "none" }}
                    onContextMenu={preventImageDownload}
                    onDragStart={preventImageDownload}
                    onClick={() => setIsModalOpen(false)}
                  />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
